'use client';
import { useEffect, useState, useCallback } from 'react';
import { client } from '@/lib/contentful';

type ContentfulResponse<T> = {
  items: T[];
  total: number;
  skip: number;
  limit: number;
};

type PressReleaseSnippet = {
  sys: {
    id: string;
  };
  fields: {
    date: string;
    entryName: string;
    heading: string;
    richText: object;
  };
};

const usePressReleaseCollection = () => {
  const [entries, setEntries] = useState<PressReleaseSnippet[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [totalEntries, setTotalEntries] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const entriesPerPage = 5;

  const fetchData = useCallback(async () => {
    setStatus('loading');

    try {
      const response: ContentfulResponse<PressReleaseSnippet> =
        await client.getEntries({
          content_type: 'pressReleaseSnippet',
          limit: entriesPerPage,
          skip: (currentPage - 1) * entriesPerPage,
          order: '-fields.date',
        });

      setEntries(response.items);
      setTotalEntries(response.total);
      setStatus('idle');
    } catch (error) {
      console.warn('Error occurred while fetching entries:', error);
      setStatus('error');
    }
  }, [currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (pageNumber: number) => {
    setHasInteracted(true);
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  );

  const truncatePagination = () => {
    const minPagesForPortioning = 5;
    if (pageNumbers.length < minPagesForPortioning) return pageNumbers;

    const currentElIndex = currentPage - 1;
    let startRange = Math.max(currentElIndex - 2, 0);
    let endRange = Math.min(currentElIndex + 2, pageNumbers.length - 1);

    // Adjust range if current page is near start or end
    if (currentElIndex === 0) endRange += 2;
    else if (currentElIndex === 1) endRange += 1;
    if (currentElIndex === pageNumbers.length - 1) startRange -= 2;
    else if (currentElIndex === pageNumbers.length - 2) startRange -= 1;

    // Always include first and last pages
    const portion = pageNumbers.slice(startRange, endRange + 1);
    if (!portion.includes(1)) portion.unshift(1);
    if (!portion.includes(pageNumbers.length)) portion.push(pageNumbers.length);

    return portion;
  };

  const truncatedPageNumbers = truncatePagination();
  const renderedPageNumbers = [];

  for (let i = 0; i < truncatedPageNumbers.length; i++) {
    const currentPageNumber = truncatedPageNumbers[i];
    const previousPageNumber = truncatedPageNumbers[i - 1];

    if (previousPageNumber && previousPageNumber < currentPageNumber - 1) {
      renderedPageNumbers.push(
        <span
          key={`ellipsis-${i}`}
          className="cursor-default text-grey-midtone"
        >
          ...
        </span>,
      );
    }

    renderedPageNumbers.push(
      <button
        key={currentPageNumber}
        onClick={() => handlePageChange(currentPageNumber)}
        className={`m-0 mx-1 p-1 text-d-base ${
          currentPageNumber === currentPage ? '' : 'text-grey-midtone'
        }`}
      >
        {currentPageNumber}
      </button>,
    );
  }

  return {
    entries,
    status,
    truncatedPageNumbers,
    renderedPageNumbers,
    handlePageChange,
    totalPages,
    currentPage,
    hasInteracted,
  };
};

export default usePressReleaseCollection;
